import { Inject, Injectable, Injector } from '@angular/core';
import Event from '@bugsnag/core/types/event';
import Bugsnag from '@bugsnag/js';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { Environment, ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { filter } from 'rxjs/operators';
import { SHA } from '../constants/injector.constants';

@Injectable({ providedIn: 'root' })
export class GlobalErrorHandler extends BugsnagErrorHandler {
    constructor(@Inject(SETTINGS) private _settings: ISettings, injector: Injector) {
        super(
            Bugsnag.start({
                apiKey: _settings.bugsnagApiKey,
                appVersion: injector.get(SHA)?.sha,
                onError: (event: Event) => {
                    if (this._shouldIgnore(event)) {
                        return false;
                    } else if (
                        // The first error is the main error.
                        event.errors[0]?.errorClass === 'HttpErrorResponse' ||
                        event.errors[0]?.errorClass === 'ErrorResponse' ||
                        (event.errors[0]?.errorClass === 'Error' &&
                            event.errors[0]?.errorMessage.indexOf('CSRF') >= 0)
                    ) {
                        // Change `severity` to 'info' for HttpErrors so that we can avoid a FrontEnd page.
                        event.severity = 'info';
                    }
                },
                releaseStage: Environment[_settings.environment]
            })
        );

        // This must be in a setTimeout or a circular dependency will happen
        // https://github.com/angular/angular/issues/18224
        setTimeout(() => {
            injector
                .get(UserService)
                .user$.pipe(filter((user) => !!user))
                .subscribe((user) => {
                    if (this.bugsnagClient) {
                        Bugsnag.setUser(
                            user.userKey,
                            `${user.firstName} ${user.lastName}`,
                            user.emailAddress
                        );
                        // Add user to metadata.
                        Bugsnag.addMetadata('user', user);
                    }
                });

            if (this.bugsnagClient) {
                // Add app settings metadata.
                Bugsnag.addMetadata('settings', this._settings);
            }
        });
    }

    private _shouldIgnore(event: Event): boolean {
        return (
            this._settings.environment === Environment.Development ||
            this._isTableauIframeError(event)
        );
    }

    private _isTableauIframeError(event: Event): boolean {
        return (
            !!window.location.href.match(/\/buyer/gi) &&
            !!event.errors[0]?.errorMessage?.match(/unsafe value used in a resource URL context/g)
        );
    }
}
